import type { Bank } from "./components/BankOptions";

/** What the server hands back once a bank is chosen. */
export type BankHandoff =
  | { type: "redirect"; url: string }
  | {
      type: "form";
      form: {
        action: string;
        method: "POST";
        fields: Record<string, string>;
        /** The server's POSTBANK_DRY_RUN: never post this form. */
        dryRun?: boolean;
      };
    };

/** Prints every field the browser would post to BORICA, M_INFO decoded. */
function logPostbankPayload(form: Extract<BankHandoff, { type: "form" }>["form"]) {
  console.group(`[PostBank] ${form.method} ${form.action}`);
  console.table(form.fields);
  if (form.fields.M_INFO) {
    try {
      console.log("M_INFO (decoded):", JSON.parse(atob(form.fields.M_INFO)));
    } catch {
      console.log("M_INFO could not be decoded:", form.fields.M_INFO);
    }
  }
  console.log("Raw payload:", JSON.stringify(form, null, 2));
  console.groupEnd();
}

/**
 * Sends the browser to the bank. DSK takes a link; BORICA takes the purchase as
 * a form POST, so its signed fields are posted from a throwaway form.
 *
 * Returns false when the browser was not sent anywhere, so the caller can
 * re-enable its buttons.
 */
export function goToBank(handoff: BankHandoff, bank?: Bank): boolean {
  if (handoff.type === "redirect") {
    window.location.href = handoff.url;
    return true;
  }

  logPostbankPayload(handoff.form);

  // POSTBANK_DRY_RUN on the server: the payload is logged above and the browser
  // stops here, before any form exists, so nothing reaches BORICA.
  if (handoff.form.dryRun) {
    console.warn(`[${bank ?? "postbank"}] POSTBANK_DRY_RUN is on — payload NOT sent to BORICA.`);
    return false;
  }

  const { action, method, fields } = handoff.form;
  const form = document.createElement("form");
  form.action = action;
  form.method = method;
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
  return true;
}
